import { useEffect, useState } from "react";
import { Bell, CheckCircle2, Clock, Mail, MessageCircle, RefreshCcw, Send, XCircle } from "lucide-react";
import { toast } from "sonner";
import { adminApi, formatApiError } from "../lib/api";

const CHANNEL_TONE = {
  sent: "text-emerald-300", failed: "text-red-300", retry: "text-amber-300",
  awaiting_setup: "text-amber-300", blocked: "text-red-300", skipped: "text-mist-2", disabled: "text-mist-2",
};

function StatusCard({ label, value, sub, ok, testid }) {
  return (
    <div className="card-surface rounded-2xl p-4" data-testid={testid}>
      <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-mist-2">{label}</p>
      <p className={`mt-2 font-display text-2xl font-bold ${ok ? "text-emerald-300" : "text-amber-300"}`}>{value}</p>
      {sub && <p className="mt-1 text-[11px] leading-relaxed text-mist-2">{sub}</p>}
    </div>
  );
}

export default function AdminNotifications({ token }) {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState("");
  const [lastTest, setLastTest] = useState(null);

  const load = () => adminApi.notifications(token).then(setData).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []); // eslint-disable-line

  const save = async (patch) => {
    setSaving(true);
    try {
      await adminApi.saveNotifySettings(patch, token);
      toast.success("Notification settings saved");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async (channel) => {
    setTesting(channel);
    try {
      const res = await adminApi.testNotify(channel, token);
      setLastTest(res.result);
      toast.success(`Test ${channel} triggered — see result below`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setTesting("");
    }
  };

  const retry = async () => {
    try {
      const res = await adminApi.retryNotify(token);
      toast.success(`${res.requeued} notification(s) requeued`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!data) return <div className="flex h-40 items-center justify-center text-sm text-mist-2">Loading notification status…</div>;
  const { config, stats, recent } = data;

  return (
    <div className="flex flex-col gap-6" data-testid="admin-notifications-panel">
      {/* status cards */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatusCard label="Gmail / email status" ok={config.email_configured}
          value={config.email_configured ? (config.email_transport === "gmail_api" ? "Gmail API" : "Active") : "Not set up"}
          sub={`To: ${config.destination_email || "—"}`} testid="notify-stat-email" />
        <StatusCard label="WhatsApp status" ok={config.whatsapp_configured}
          value={config.whatsapp_configured ? "Active" : "Awaiting setup"}
          sub={`To: ${config.destination_whatsapp || "—"}`} testid="notify-stat-whatsapp" />
        <StatusCard label="Pending / retrying" ok={!stats.pending && !stats.retry}
          value={`${stats.pending + stats.retry}`}
          sub={`Worker tick: ${stats.worker_last_tick ? new Date(stats.worker_last_tick).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "—"} · Last sent: ${stats.last_sent_at ? new Date(stats.last_sent_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—"}`} testid="notify-stat-pending" />
        <StatusCard label="Failed" ok={!stats.failed} value={`${stats.failed}`}
          sub={stats.failed ? "Use Retry failed below" : "All clear"} testid="notify-stat-failed" />
      </div>

      {/* setup checklists */}
      {!config.whatsapp_configured && (
        <div className="rounded-2xl border border-amber-400/30 bg-amber-400/[0.07] p-5" data-testid="notify-whatsapp-checklist">
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-amber-300">WhatsApp setup checklist — messages are not sending yet</p>
          <ol className="mt-3 flex list-decimal flex-col gap-1.5 pl-5 text-[12.5px] leading-relaxed text-mist">
            <li>Create a Meta app at developers.facebook.com and add the <span className="text-paper">WhatsApp</span> product</li>
            <li>In WhatsApp → API Setup, copy the <span className="text-paper">Phone Number ID</span> and generate an access token (use a System User token for production)</li>
            <li>Create an approved template named <span className="text-paper">owner_notification</span> (English, one body variable)</li>
            <li>Add <span className="text-paper">WHATSAPP_ACCESS_TOKEN</span> and <span className="text-paper">WHATSAPP_PHONE_NUMBER_ID</span> to /app/backend/.env</li>
            <li>Restart the backend — the +917011663967 destination is already configured</li>
          </ol>
        </div>
      )}
      {!config.email_configured && (
        <div className="rounded-2xl border border-amber-400/30 bg-amber-400/[0.07] p-5" data-testid="notify-email-checklist">
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-amber-300">Email setup checklist</p>
          <ol className="mt-3 flex list-decimal flex-col gap-1.5 pl-5 text-[12.5px] leading-relaxed text-mist">
            <li>Set <span className="text-paper">CARZA_NOTIFICATION_EMAIL</span> (the managed email key is already provisioned)</li>
            <li>Optional Gmail API path: set <span className="text-paper">GOOGLE_OAUTH_CLIENT_ID</span>, <span className="text-paper">GOOGLE_OAUTH_CLIENT_SECRET</span>, <span className="text-paper">GOOGLE_OAUTH_REFRESH_TOKEN</span> — email then sends from your own Gmail via the Gmail API</li>
            <li>Restart the backend</li>
          </ol>
        </div>
      )}

      {/* controls */}
      <div className="card-surface rounded-2xl p-5" data-testid="notify-controls">
        <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-mist-2">Controls</p>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          {[
            { key: "email_enabled", label: "Email notifications", on: config.email_enabled, icon: Mail },
            { key: "whatsapp_enabled", label: "WhatsApp notifications", on: config.whatsapp_enabled, icon: MessageCircle },
          ].map((t) => (
            <button key={t.key} onClick={() => save({ [t.key]: !t.on })} disabled={saving}
              className={`flex items-center gap-2 rounded-full border px-4 py-2 text-[13px] font-semibold transition-colors ${t.on ? "border-emerald-400/40 bg-emerald-400/10 text-emerald-300" : "border-white/10 text-mist"}`}
              data-testid={`notify-${t.key.replace("_", "-")}-toggle`}>
              <t.icon size={14} /> {t.label}: {t.on ? "ON" : "OFF"}
            </button>
          ))}
        </div>
        <p className="mt-5 font-mono text-[10px] uppercase tracking-[0.22em] text-mist-2">Event categories</p>
        <div className="mt-2 flex flex-wrap gap-2">
          {Object.entries(config.categories).map(([cat, on]) => (
            <button key={cat} onClick={() => save({ categories: { ...config.categories, [cat]: !on } })} disabled={saving}
              className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[12px] font-medium capitalize transition-colors ${on ? "border-carza/40 bg-carza-dim text-carza-lite" : "border-white/10 text-mist-2 line-through"}`}
              data-testid={`notify-cat-${cat}`}>
              {on ? <CheckCircle2 size={11} /> : <XCircle size={11} />} {cat}{on ? "" : " (off)"}
            </button>
          ))}
        </div>
        <div className="mt-5 flex flex-wrap gap-2.5">
          <button onClick={() => sendTest("email")} disabled={testing} className="btn btn-primary px-5 py-2.5 text-[12.5px] disabled:opacity-50" data-testid="notify-test-email-btn">
            <Send size={13} /> {testing === "email" ? "Sending…" : "Send test email"}
          </button>
          <button onClick={() => sendTest("whatsapp")} disabled={testing} className="btn btn-outline px-5 py-2.5 text-[12.5px] disabled:opacity-50" data-testid="notify-test-whatsapp-btn">
            <MessageCircle size={13} /> {testing === "whatsapp" ? "Sending…" : "Send test WhatsApp"}
          </button>
          <button onClick={retry} className="btn btn-outline px-5 py-2.5 text-[12.5px]" data-testid="notify-retry-btn">
            <RefreshCcw size={13} /> Retry failed
          </button>
        </div>
        {lastTest && (
          <div className="mt-4 rounded-2xl border border-white/10 bg-white/[0.02] p-4 text-[12px]" data-testid="notify-test-result">
            <p className="text-mist">Last test result: <span className={`font-semibold ${CHANNEL_TONE[lastTest.status] || "text-paper"}`}>{lastTest.status}</span></p>
            {Object.entries(lastTest.channels || {}).map(([ch, c]) => (
              <p key={ch} className="mt-1 text-mist-2">{ch}: {c.status}{c.detail ? ` — ${c.detail}` : ""}</p>
            ))}
          </div>
        )}
      </div>

      {/* recent log */}
      <div className="card-surface rounded-2xl p-5" data-testid="notify-recent">
        <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-mist-2"><Bell size={12} /> Notification audit log (latest 25)</p>
        {recent.length === 0 ? (
          <p className="mt-4 text-sm text-mist-2">No notifications yet. Business events will appear here.</p>
        ) : (
          <div className="mt-4 flex flex-col gap-2">
            {recent.map((r) => (
              <div key={r.event_id} className="rounded-xl border border-white/5 bg-white/[0.02] px-4 py-3 text-[12.5px]" data-testid={`notify-row-${r.event_id.slice(0, 24)}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-semibold text-paper">{r.title}</span>
                  <span className="flex items-center gap-3 font-mono text-[10px]">
                    {(r.channels || {}).email && <span className={CHANNEL_TONE[(r.channels.email || {}).status] || "text-mist-2"}><Mail size={11} className="inline" /> {r.channels.email.status}</span>}
                    {(r.channels || {}).whatsapp && <span className={CHANNEL_TONE[(r.channels.whatsapp || {}).status] || "text-mist-2"}><MessageCircle size={11} className="inline" /> {r.channels.whatsapp.status}</span>}
                    <span className="text-mist-2"><Clock size={10} className="inline" /> {String(r.created_at || "").slice(0, 16).replace("T", " ")}</span>
                  </span>
                </div>
                <p className="mt-1 text-[11px] text-mist-2">{r.category} · attempts: {r.attempts}{r.last_error ? ` · ${r.last_error}` : ""}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
